import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api.js';
import Navigation from '../components/Navigation.js';
import { LAYOUT_CONFIG } from '../constants/layout.js';

// 每页记录数量
const PAGE_SIZE = 30;

const ListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 状态管理
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [totalPages, setTotalPages] = useState(0);

  // 筛选和搜索状态
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // refs
  const listRef = useRef(null);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  // 构建API查询参数
  const buildQueryParams = useCallback((page = 1) => {
    const params = {
      page: page,
      limit: PAGE_SIZE,
      sort: 'created_at:desc'
    };

    // 添加收藏筛选
    if (filterFavorites) {
      params['filter[fav]'] = 'true';
    }

    // 添加搜索查询
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    return params;
  }, [filterFavorites, searchQuery]);

  // 检查是否还有更多数据
  const hasMoreData = currentPage < totalPages && totalPages > 0;

  // 加载下一页数据
  const loadNextPage = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (loadingRef.current || !hasMoreData) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log(`📄 加载第 ${nextPage} 页数据`);

      const response = await apiService.getRecords(buildQueryParams(nextPage));

      if (response.success && response.data) {
        // 适配新的API响应格式
        const newRecords = response.data.items || response.data.records || [];

        setRecords(prev => [...prev, ...newRecords]);

        // 从新的meta结构中获取分页信息
        if (response.data.meta?.pagination) {
          const pagination = response.data.meta.pagination;
          setTotal(pagination.total);
          setTotalPages(pagination.totalPages);
        } else {
          // 兼容旧格式
          setTotal(response.data.total || 0);
          setTotalPages(response.data.totalPages || 1);
        }

        setCurrentPage(nextPage);

        console.log(`✅ 第 ${nextPage} 页加载完成，共 ${newRecords.length} 条记录`);
      } else {
        throw new Error('获取记录失败');
      }
    } catch (err) {
      console.error('❌ 加载记录失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [currentPage, hasMoreData, buildQueryParams]);

  // 初始化加载第一页和重置功能
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('📄 加载初始数据');

      const response = await apiService.getRecords(buildQueryParams(1));

      if (response.success && response.data) {
        // 适配新的API响应格式
        const records = response.data.items || response.data.records || [];

        setRecords(records);

        // 从新的meta结构中获取分页信息
        if (response.data.meta?.pagination) {
          const pagination = response.data.meta.pagination;
          setTotal(pagination.total);
          setTotalPages(pagination.totalPages);
        } else {
          // 兼容旧格式
          setTotal(response.data.total || 0);
          setTotalPages(response.data.totalPages || 1);
        }

        setCurrentPage(1);

        console.log(`✅ 初始数据加载完成，共 ${records.length} 条记录`);
      } else {
        throw new Error('获取记录失败');
      }
    } catch (err) {
      console.error('❌ 加载初始数据失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  // 初始化加载第一页
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // 重置分页的函数（保留以备将来使用）
  // const resetPagination = useCallback(() => {
  //   setRecords([]);
  //   setCurrentPage(1);
  //   setTotal(0);
  //   setTotalPages(0);
  //   loadInitialData();
  // }, [loadInitialData]);

  // 监听筛选状态变化
  useEffect(() => {
    // 重置并重新加载数据
    setRecords([]);
    setCurrentPage(1);
    setTotal(0);
    setTotalPages(0);
    loadInitialData();
  }, [filterFavorites, searchQuery, loadInitialData]);

  // Intersection Observer 实现无限滚动 - 优化触发距离
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMoreData) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          loadNextPage();
        }
      },
      {
        rootMargin: '200px', // 增加触发距离，提供更流畅的体验
        threshold: 0.1       // 降低阈值，更早触发加载
      }
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMoreData, loadNextPage]);

  // 处理从详情页返回时的逻辑
  useEffect(() => {
    if (location.state?.from === 'detail' && location.state?.clickedIndex !== undefined && records.length > 0) {
      console.log('🔄 检测到从详情页返回，准备滚动到对应记录:', {
        clickedIndex: location.state.clickedIndex,
        recordsCount: records.length
      });

      // 延迟确保DOM完全渲染
      setTimeout(() => {
        const targetIndex = location.state.clickedIndex;
        const targetElement = document.querySelector(`[data-record-index="${targetIndex}"]`);

        if (targetElement) {
          console.log('🎯 找到目标记录元素，滚动到视图中');
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // 高亮显示对应记录（不自动消失）
          setHighlightedIndex(targetIndex);
        } else {
          console.warn('⚠️ 未找到目标记录元素，索引:', targetIndex);
          setHighlightedIndex(targetIndex);
        }
      }, 500);
    }
  }, [location.state, records.length]);

  const toggleFavorite = useCallback(async (audioId) => {
    try {
      // 找到当前记录的收藏状态
      const record = records.find(r => r.audioId === audioId);
      if (!record) return;

      // 调用API更新收藏状态
      await apiService.updateRecord(audioId, {
        fav: !record.fav
      });

      // 更新本地状态
      setRecords(records.map(record =>
        record.audioId === audioId ? { ...record, fav: !record.fav } : record
      ));
    } catch (err) {
      console.error('更新收藏状态失败:', err);
    }
  }, [records]);

  const goToDetail = useCallback((audioId, index) => {
    navigate(`/recording/${audioId}`, {
      state: {
        from: 'list',
        clickedIndex: index,
        scrollPosition: window.scrollY
      }
    });
  }, [navigate]);

  // 格式化时间显示
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, []);

  // 获取显示的文本内容，优先展示润色后的内容
  const getDisplayText = useCallback((record) => {
    // 优先展示润色后的文本
    if (record.polishedText && record.polishedText.trim()) {
      return record.polishedText.trim();
    }
    // 如果润色文本为空，则展示转写文本
    if (record.transcriptionText && record.transcriptionText.trim()) {
      return record.transcriptionText.trim();
    }
    // 都为空则显示默认文本
    return '暂无内容';
  }, []);

  // 截断文本为一行
  const truncateToOneLine = useCallback((text, maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }, []);

  // 搜索高亮文本
  const highlightSearchText = useCallback((text, searchQuery) => {
    if (!searchQuery || !text) return text;

    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchQuery.toLowerCase()
        ? <span key={index} className="bg-yellow-500 text-black px-0.5 rounded">{part}</span>
        : part
    );
  }, []);

  // 筛选按钮点击处理
  const toggleFilterFavorites = useCallback(() => {
    setFilterFavorites(prev => !prev);
    setRecords([]);
    setCurrentPage(1);
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* 固定标题栏 */}
      <div className={`sticky top-0 z-10 bg-dark-bg border-b border-gray-800 ${LAYOUT_CONFIG.PAGE_PADDING} py-4 backdrop-blur-sm bg-opacity-95`}>
        <div className={`${LAYOUT_CONFIG.PAGE_MAX_WIDTH} mx-auto`}>
          {/* 第一行：标题和统计信息 */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-favorite-yellow text-xl">⭐</span>
              <h1 className="text-xl font-medium">录音列表</h1>
              {total > 0 && (
                <span className="text-gray-400 text-sm">
                  {filterFavorites && '收藏：'}{records.length}/{total.toLocaleString()} 条记录
                  {totalPages > 1 && `，第 ${currentPage}/${totalPages} 页`}
                  {searchQuery && '，已筛选'}
                </span>
              )}
            </div>
          </div>

          {/* 第二行：搜索和筛选控件 */}
          <div className="flex items-center justify-end space-x-3">
            {/* 搜索框 */}
            <div className="relative">
              <input
                type="text"
                placeholder="搜索录音内容..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setRecords([]);
                  setCurrentPage(1);
                }}
                className="bg-gray-800 text-white px-3 py-1.5 pr-8 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setRecords([]);
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ×
                </button>
              )}
            </div>

            {/* 收藏筛选按钮 */}
            <button
              onClick={toggleFilterFavorites}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterFavorites
                  ? 'bg-favorite-yellow text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {filterFavorites ? '⭐ 已筛选' : '⭐ 筛选收藏'}
            </button>
          </div>
        </div>
      </div>

      {/* 错误状态 */}
      {error && (
        <div className={`flex flex-col items-center justify-center py-12 ${LAYOUT_CONFIG.PAGE_PADDING}`}>
          <div className="text-red-400 mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            重试
          </button>
        </div>
      )}

      {/* 空状态 */}
      {!loading && !error && total === 0 && (
        <div className={`flex flex-col items-center justify-center py-12 ${LAYOUT_CONFIG.PAGE_PADDING}`}>
          <div className="text-gray-400 mb-2">暂无录音记录</div>
          <button
            onClick={() => navigate('/recording')}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            去录音
          </button>
        </div>
      )}

      {/* 记录列表 */}
      {!error && (
        <div className={`${LAYOUT_CONFIG.PAGE_PADDING} py-2 pb-32`} ref={listRef}>
          <div className={`${LAYOUT_CONFIG.PAGE_MAX_WIDTH} mx-auto`}>
            {records.map((record, index) => {
              const isHighlighted = index === highlightedIndex;
              console.log(`🔍 记录${index}:`, {
                audioId: record.audioId,
                id: record.id,
                createdAt: record.createdAt,
                hasContent: !!(record.transcriptionText || record.polishedText)
              });
              return (
                <div
                  key={record.audioId || `record-${index}`}
                  data-record-index={index}
                  onClick={() => goToDetail(record.audioId, index)}
                  className={`rounded-lg p-4 mb-3 cursor-pointer transition-all duration-300 ${
                    isHighlighted
                      ? 'bg-blue-900 border border-blue-400 shadow-lg shadow-blue-500/50'
                      : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                >
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4 min-w-0">
                    <p className="text-white mb-2 truncate">
                      {searchQuery
                        ? highlightSearchText(truncateToOneLine(getDisplayText(record)), searchQuery)
                        : truncateToOneLine(getDisplayText(record))
                      }
                    </p>
                    <p className="text-gray-500 text-sm truncate">
                      {formatDate(record.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(record.audioId);
                    }}
                    className="text-2xl hover:scale-110 transition-transform flex-shrink-0"
                  >
                    {record.fav ? '⭐' : '☆'}
                  </button>
                </div>
              </div>
            );
          })}

          {/* 无限滚动触发器 */}
          {hasMoreData && (
            <div ref={sentinelRef} className="h-1" />
          )}

          {/* 加载更多指示器 */}
          {loading && (
            <div className="flex justify-center py-4">
              <div className="text-gray-400 flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span>加载中...</span>
              </div>
            </div>
          )}

          {/* 没有更多数据提示 */}
          {!hasMoreData && records.length > 0 && (
            <div className="flex justify-center py-4">
              <div className="text-gray-400">
                已加载全部记录 ({records.length} 条)
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* 底部导航 */}
      <Navigation />
    </div>
  );
};

export default ListPage;